import { useCallback, useState } from 'react';

import api from '~/services/api';
import { useStoreActions } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { normalizeDateString } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';

import { IAddAllocationFormValues } from '../../types';
import { useAddClientAllocationContext } from '../../context/AddClientAllocationContext';
import {
  groupAllocationsToAdd,
  groupLocationGroupAllocationsToAdd,
} from '../../../EditClientAllocation/utils';

const useAddAllocationSubmit = () => {
  const { actTeamId, clinicId, locationGroups } = useAddClientAllocationContext();
  const { isGlobalUser, isActTeamMember } = useRole();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showError } = useStoreActions(actions => actions.snackbar);

  const submit = useCallback(
    async (values: IAddAllocationFormValues): Promise<boolean> => {
      const { dateRange, includeWeekends, teamMembersAllocations } = values;

      const regularAllocations = groupAllocationsToAdd(teamMembersAllocations);
      const locationGroupAllocations = groupLocationGroupAllocationsToAdd(
        teamMembersAllocations,
        locationGroups,
      );

      if (!regularAllocations.length && !locationGroupAllocations.length) {
        return false;
      }

      setIsSubmitting(true);

      const datePayload = {
        from: normalizeDateString(dateRange[0]),
        to: normalizeDateString(dateRange[1], true),
        includeWeekends,
      };

      try {
        if (isGlobalUser) {
          const myAllocation = regularAllocations[0] ?? locationGroupAllocations[0];
          if (myAllocation) {
            await api.clientAllocation.addOwnClientAllocationGU(
              { clinicId },
              {
                ...datePayload,
                clients: myAllocation.clients,
                locationGroupIds: myAllocation.locationGroupIds,
              },
            );
          }
        } else if (isActTeamMember) {
          const myAllocation = regularAllocations[0] ?? locationGroupAllocations[0];
          if (myAllocation) {
            await api.clientAllocation.addOwnClientAllocation(
              { clinicId, teamId: actTeamId },
              {
                ...datePayload,
                clients: myAllocation.clients,
                locationGroupIds: myAllocation.locationGroupIds,
              },
            );
          }
        } else {
          const combinedAllocations = [...regularAllocations, ...locationGroupAllocations].reduce<
            typeof regularAllocations
          >((acc, item) => {
            const existing = acc.find(a => a.userId === item.userId);
            if (existing) {
              existing.clients.push(...item.clients);
              existing.locationGroupIds.push(...item.locationGroupIds);
            } else {
              acc.push({ ...item });
            }
            return acc;
          }, []);

          if (combinedAllocations.length) {
            await api.clientAllocation.addClientAllocation(
              { clinicId, teamId: actTeamId },
              { ...datePayload, allocations: combinedAllocations },
            );
          }
        }

        return true;
      } catch (error) {
        showError(extractErrorMessage(error));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [actTeamId, clinicId, isGlobalUser, isActTeamMember, locationGroups, showError],
  );

  return { submit, isSubmitting };
};

export default useAddAllocationSubmit;
